import { IconButton } from "@dropbox/dig-components/buttons";
import { ThemeContainer, ThemeProvider } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { ListViewLine } from "@dropbox/dig-icons/assets";
import { DashWordmark, WordmarkLogo } from "@dropbox/dig-logos";
import { useStackPageAugustRevisionEnabled } from "@mirage/august-revision-hook";
import { MobileDownloadBanner } from "@mirage/growth/components/MobileDownloadBanner";
import { OmniboxErrorBoundary } from "@mirage/mosaics/Omnibox/components/OmniboxErrorBoundary";
import { useDashTrialInfo } from "@mirage/service-dash-trial/hooks/useDashTrialInfo";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useDualMode } from "@mirage/service-experimentation/useDualMode";
import { useStackChatEnabled } from "@mirage/service-experimentation/useStackChatEnabled";
import { BetaBadge } from "@mirage/shared/badges/BetaBadge";
import { getDashActionSurface } from "@mirage/shared/hooks/useDashActionSurface";
import { onKeyDownCommitFn } from "@mirage/shared/util/on-key-down";
import { activeStackAtom } from "@mirage/stacks/atoms";
import { useCurrentBackgroundTheme } from "@mirage/stacks/themes";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { useAtom, useAtomValue } from "jotai";
import { useLocation, useNavigate } from "react-router-dom";
import { DashTrialIndicatorTopNav } from "../DashTrial/components/DashTrialIndicatorTopNav";
import { globalNavIsCollapsedAtom } from "../GlobalNav/atoms";
import { useSharedOmniboxProps } from "../Omnibox/hooks/useSharedOmniboxProps";
import { PersistedOmnibox } from "../Omnibox/withPersistedOmniboxSurface";
import styles from "./TopNav.module.css";

type TopNavProps = {
  isAugustRevisionEnabled: boolean;
  isDesktopExperience: boolean;
  isMobileExperience: boolean;
  isAIOmniboxEnabled: boolean;
  hideTopNavSearchBar: boolean;
};

export const TopNav = ({
  isAugustRevisionEnabled,
  isDesktopExperience,
  isMobileExperience,
  isAIOmniboxEnabled,
  hideTopNavSearchBar,
}: TopNavProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dashActionSurface = getDashActionSurface(pathname);
  const isChatPage = dashActionSurface === "conversation_page";
  const shouldShowDashTrialIndicator = [
    "start_page",
    "stacks_page",
    "stack_details_page",
  ].includes(dashActionSurface ?? "");
  const { pageScrolled } = useSharedOmniboxProps();
  const { isDualModeLauncher } = useDualMode();
  const isDarwinHornet =
    EnvCtx.platform === "darwin" && EnvCtx.surface === "desktop";

  const stackFromFullStackPage = useAtomValue(activeStackAtom);
  const backgroundTheme = useCurrentBackgroundTheme(stackFromFullStackPage);
  const isStackChatEnabled = useStackChatEnabled();
  const isAugustStackPageEnabled = useStackPageAugustRevisionEnabled();
  const { displayDashTrialIndicator } = useDashTrialInfo();
  const [isCollapsed, setIsCollapsed] = useAtom(globalNavIsCollapsedAtom);

  if (isDesktopExperience && !isAIOmniboxEnabled) {
    return isChatPage ? null : (
      <ThemeProvider overrides={backgroundTheme}>
        <ThemeContainer>
          <OmniboxErrorBoundary>
            <PersistedOmnibox shouldMemoize={isAIOmniboxEnabled} />
          </OmniboxErrorBoundary>
        </ThemeContainer>
      </ThemeProvider>
    );
  }

  if (isMobileExperience) {
    return (
      <ThemeProvider overrides={backgroundTheme}>
        <ThemeContainer>
          <div className={classNames(styles.container)}>
            <MobileDownloadBanner />
            <div
              className={classNames(styles.navContainer, {
                [styles.mobile]: true,
                [styles.noSearchBar]: hideTopNavSearchBar,
                [styles.augustRevision]: isAugustRevisionEnabled,
                [styles.darwinHornet]: isDarwinHornet,
                [styles.showBackground]:
                  isAugustStackPageEnabled &&
                  !isStackChatEnabled &&
                  backgroundTheme !== undefined,
                [styles.scrollingBorder]: pageScrolled && !isDesktopExperience,
              })}
            >
              <div className={styles.leftContainer}>
                <IconButton
                  className={styles.noShrink}
                  shape="circular"
                  variant="transparent"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  aria-label={i18n.t(
                    isCollapsed ? "expand_nav_aria" : "collapse_nav_aria",
                  )}
                  aria-expanded={!isCollapsed}
                >
                  <UIIcon src={ListViewLine} />
                </IconButton>
              </div>
              <>
                <div
                  tabIndex={0}
                  className={styles.centerContainer}
                  role="button"
                  aria-label={i18n.t("home")}
                  onClick={() => navigate("/")}
                  onKeyDown={onKeyDownCommitFn(() => navigate("/"))}
                >
                  <WordmarkLogo
                    size={32}
                    src={DashWordmark}
                    color="var(--dig-color__text__base)"
                  />
                  <BetaBadge excludeDfb />
                </div>
                {!hideTopNavSearchBar && (
                  <div className={styles.mobileSearchBar}>
                    <OmniboxErrorBoundary>
                      <PersistedOmnibox shouldMemoize={isAIOmniboxEnabled} />
                    </OmniboxErrorBoundary>
                  </div>
                )}
              </>
            </div>
          </div>
        </ThemeContainer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider overrides={backgroundTheme}>
      <ThemeContainer>
        <div className={classNames(styles.container)}>
          <div
            className={classNames(styles.navContainer, {
              [styles.noSearchBar]: hideTopNavSearchBar,
              [styles.augustRevision]: isAugustRevisionEnabled,
              [styles.showBackground]:
                isAugustStackPageEnabled &&
                !isStackChatEnabled &&
                backgroundTheme !== undefined,
              [styles.scrollingBorder]: pageScrolled && !isDesktopExperience,
              [styles.aiOmnibox]: isAIOmniboxEnabled,
              [styles.fullWidthSearchbar]:
                isAIOmniboxEnabled && isDualModeLauncher,
            })}
          >
            <div className={styles.leftContainer}></div>
            {!hideTopNavSearchBar && (
              <OmniboxErrorBoundary>
                <PersistedOmnibox shouldMemoize={isAIOmniboxEnabled} />
              </OmniboxErrorBoundary>
            )}
          </div>
          {displayDashTrialIndicator && shouldShowDashTrialIndicator && (
            <DashTrialIndicatorTopNav />
          )}
        </div>
      </ThemeContainer>
    </ThemeProvider>
  );
};
